import { z } from 'zod';

import { BlockValueFieldSchemaV2, BlockValueFieldSchemaVariant } from './blockValueFieldSchema.js';
import { BlockValueTemplateHint, validateBlockValueTemplate } from './template.js';

/**
 * @arg value - A string that is expected to be a number.
 * @returns The number value of the string.
 * @throws {RangeError} If the string is not a valid number.
 * FIXME: Empty string should not be parsed as a number.
 * https://app.asana.com/1/1187495389489253/project/1211038721907666/task/1211185023182384?focus=true
 */
export function parseAsNumber(value: string): number {
  const numberValue = Number(value);
  if (Number.isNaN(numberValue)) {
    throw new RangeError('Invalid number.');
  }
  return numberValue;
}

/**
 * @arg numberValidationSchema - User supplied zod number schema that is checked if value is not a block
 * value template. e.g. `z.number().min(0).max(100)`
 * @returns A Zod schema for validating values that are either block value templates or some zod number type;
 * The returned schema validates that value can parse into number using {@link parseAsNumber}, but only returns
 * the original input string value.
 */
export function BlockValueNumberSchema(args: {
  numberValidationSchema: z.ZodType<number, string | number>;
}) {
  return z.coerce.string().superRefine((stringValue, context) => {
    const templateParseResult = BlockValueFieldSchemaV2({
      variant: BlockValueFieldSchemaVariant.TemplateOnly,
    }).safeParse(stringValue);
    if (templateParseResult.success) {
      return;
    }

    // We want to validate against string schema only if string is not template-like
    const templateValidation = validateBlockValueTemplate(stringValue);
    if (templateValidation.hint !== BlockValueTemplateHint.NotTemplateLike) {
      templateParseResult.error.issues.forEach((issue) => {
        context.addIssue(issue as Parameters<typeof context.addIssue>[0]);
      });
      return;
    }

    let numberValue: number;
    try {
      numberValue = parseAsNumber(stringValue);
      const numberParseResult = args.numberValidationSchema.safeParse(numberValue);
      if (numberParseResult.success) {
        return;
      }
      numberParseResult.error.issues.forEach((issue) => {
        context.addIssue(issue as Parameters<typeof context.addIssue>[0]);
      });
    } catch (error) {
      if (error instanceof Error) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: error.message,
        });
      }
    }
    templateParseResult.error.issues.forEach((issue) => {
      context.addIssue(issue as Parameters<typeof context.addIssue>[0]);
    });
  });
}
